import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import moment from 'moment';
import {
  dataToForm,
  MAX_RETIREMENT_YEAR,
  MIN_RETIREMENT_YEAR,
  SocialSecurityFormValues,
  UpsertIncomeDTO,
} from './SocialSecurityForm.meta';
import { IncomeModel, IncomeType } from '../../api/useIncomeQuery';
import { useUpsertIncomeMutation } from '../../api/useUpsertIncomeMutation';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import { ADD_INCOME_SUCCESS_MESSAGE, EDIT_INCOME_SUCCESS_MESSAGE } from '../../Income.meta';
import { IncomeData } from '../../useIncome';
import { selectCurrentClient } from '../../../../CommonSelectors/CommonAreaSelector';

interface Params {
  clientId: string;
  income: IncomeModel | null;
  data: IncomeData;
  onSuccess: () => void;
}

const isBetweenMinAndMaxYear = dateOfBirth => {
  const year = moment(dateOfBirth).year();
  return year >= MIN_RETIREMENT_YEAR && year <= MAX_RETIREMENT_YEAR;
};

const ageOptionsWithFRA = ({ options, data, showMonths, isSpouse = false }) => {
  const FraYear = isSpouse ? data.SpouseFra.Year : data.PrincipalFra.Year;
  const FraMonth = isSpouse ? data.SpouseFra.Month : data.PrincipalFra.Month;

  return options.reduce((acc: { value: string | number; label: string | number }[], ageOption) => {
    if (ageOption.value === FraYear) {
      if (showMonths && FraMonth !== 0) {
        return [
          ...acc,
          ageOption,
          {
            label: `${FraYear} & ${FraMonth} ${FraMonth === 1 ? 'month' : 'months'}`,
            value: 'FRA',
            rightLabel: `Full Retirement Age${
              ageOption.rightLabel ? `, ${ageOption.rightLabel}` : ''
            }`,
          },
        ];
      }

      return [
        ...acc,
        {
          ...ageOption,
          value: 'FRA',
          rightLabel: `Full Retirement Age${
            ageOption.rightLabel ? `, ${ageOption.rightLabel}` : ''
          }`,
        },
      ];
    }

    return [...acc, ageOption];
  }, []);
};

export const useSocialSecurityForm = ({ clientId, income, data, onSuccess }: Params) => {
  const defaultValues = dataToForm(income, data);

  const { Dob: primaryDob, PartnerDob: partnerDob } = useSelector(selectCurrentClient);
  const primaryCurrentAge = moment().diff(primaryDob, 'years');
  const partnerCurrentAge = moment().diff(partnerDob, 'years');

  const primaryYob = primaryDob ? new Date(primaryDob).getFullYear() : null;
  const partnerYob = partnerDob ? new Date(partnerDob).getFullYear() : null;

  const showPrimaryMonths = isBetweenMinAndMaxYear(primaryDob);
  const showPartnerMonths = isBetweenMinAndMaxYear(partnerDob);

  const formBag = useForm<SocialSecurityFormValues>({
    defaultValues,
  });

  const [managementStyleValue, customBucketIndex] = formBag.watch([
    'orionManagementStyleId',
    'customBucketIndex',
  ]);

  const mutationBag = useUpsertIncomeMutation({ clientId });

  const earnerOptions = Object.keys(data.Options.OwnerOptions).map(key => ({
    label: key,
    options: (data.Options.OwnerOptions[key] || []).map((owner, ind) => ({
      value: owner.Name,
      label: owner.Name,
    })),
  }));

  const incomeFrequencyOptions = data.Options.IncomeFrequencyOptions;

  const ownerModel = formBag.watch('ownerModel');

  /**
   * Since Income does not have multiple Ownership, comparing values directly
   */
  const isSpouse = data.Options.OwnerOptions.People[1]?.Name === ownerModel[0].Name;

  const primaryAgeAtFillingOptions = data.Options.PrincipalSocialSecurityAgeOptions.filter(
    age => age > primaryCurrentAge,
  ).map(age => ({
    value: age,
    label: age,
    rightLabel: primaryYob ? `Year ${primaryYob + age}` : undefined,
  }));

  const partnerAgeAtFillingOptions = (data.Options.SpouseSocialSecurityAgeOptions || [])
    .filter(age => age > partnerCurrentAge)
    .map(age => ({
      value: age,
      label: age,
      rightLabel: partnerYob ? `Year ${partnerYob + age}` : undefined,
    }));

  const primaryPastAgeOptions = data.Options.PrincipalSocialSecurityAgeOptions.filter(
    age => age <= primaryCurrentAge,
  ).map(age => ({
    value: age,
    label: age,
    rightLabel: primaryYob ? `Year ${primaryYob + age}` : undefined,
  }));

  const partnerPastAgeOptions = (data.Options.SpouseSocialSecurityAgeOptions || [])
    .filter(age => age <= partnerCurrentAge)
    .map(age => ({
      value: age,
      label: age,
      rightLabel: partnerYob ? `Year ${partnerYob + age}` : undefined,
    }));

  const futureAgeOptions = isSpouse
    ? ageOptionsWithFRA({
        options: partnerAgeAtFillingOptions,
        data: data.Options,
        showMonths: showPartnerMonths,
        isSpouse: true,
      })
    : ageOptionsWithFRA({
        options: primaryAgeAtFillingOptions,
        data: data.Options,
        showMonths: showPrimaryMonths,
      });

  const fraAge = isSpouse ? data.Options.SpouseFra.Year : data.Options.PrincipalFra.Year;

  const pastAgeOptions = isSpouse
    ? ageOptionsWithFRA({
        options: partnerPastAgeOptions,
        data: data.Options,
        showMonths: showPartnerMonths,
        isSpouse: true,
      })
    : ageOptionsWithFRA({
        options: primaryPastAgeOptions,
        data: data.Options,
        showMonths: showPrimaryMonths,
      });

  const showCurrentlyReceivingBenefits = (isSpouse ? partnerCurrentAge : primaryCurrentAge) >= 60;

  const onSubmit = formBag.handleSubmit(async (values: SocialSecurityFormValues) => {
    const body: UpsertIncomeDTO = {
      clientId,
      ...values,
      id: income?.Id || null,
      incomeType: IncomeType.SocialSecurity,
      startAge: values.startAge === 'FRA' ? fraAge : values.startAge,
    };

    try {
      await mutationBag.mutateAsync(body);

      toast.success(income?.Id ? EDIT_INCOME_SUCCESS_MESSAGE : ADD_INCOME_SUCCESS_MESSAGE);

      onSuccess();
    } catch (error) {
      console.error(error);
    }
  });

  return {
    data: {
      formBag,
      mutationBag,
      earnerOptions,
      isSpouse,
      incomeFrequencyOptions,
      defaultIncomeFrequency: defaultValues.incomeFrequency,
      futureAgeOptions,
      pastAgeOptions,
      showCurrentlyReceivingBenefits,
      fraAge,
      managementStyleValue,
      customBucketIndex,
      options: data.Options,
    },
    actions: {
      onSubmit,
    },
  };
};
